import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Logger } from "shared/lib/Logger"
import { useEvent } from "shared/lib/useEvent"
import { getUserColor } from "shared/lib/user/getUserColor"
import { IconSize } from "shared/types/Icon"
import { TeamMember } from "shared/types/__generated__/graphql"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Select } from "shared/ui/input/select/select/Select"
import { TextAvatar } from "shared/ui/text-avatar/TextAvatar"

import { TeamRole } from "entities/team-members/types/TeamRole"
import { useTeamRoleOptions } from "entities/teams/model/useTeamRoleOptions"

import { TeamMemberListProps } from "../TeamMemberList"
import "../styles/_team-member-list-item.scss"
import { TeamMemberInvitationStatus } from "./TeamMemberInvitationStatus"

interface TeamMemberListItemProps
  extends Omit<TeamMemberListProps, "teamMembers"> {
  teamMember: TeamMember
}

export const TeamMemberListItem = React.memo(
  ({
    teamMember,
    isReadonly = false,
    onResendInvitation,
    onUpdate,
    onDelete
  }: TeamMemberListItemProps) => {
    const { t } = useTranslation()
    const options = useTeamRoleOptions()

    const handleDelete = useEvent(() => {
      onDelete?.(teamMember.id)
    })

    const handleRoleChange = useEvent((role: TeamRole | null) => {
      if (!role) {
        Logger.error("cannot remove role from team member")
        return
      }

      onUpdate?.({
        teamMemberID: teamMember.id,
        updates: { id: teamMember.id, role }
      })
    })

    return (
      <li
        className={classNames("team-member-list-item", {
          "team-member-list-item--readonly": isReadonly,
          "team-member-list-item--current-user": teamMember.isCurrentUser
        })}
      >
        <div className="team-member-list-item__initials">
          <TextAvatar
            initials={
              (teamMember.user?.name || teamMember.user?.email)?.charAt(0) ||
              "?"
            }
            color={getUserColor(teamMember.user?.id)}
          />
        </div>
        <div className="team-member-list-item__email-and-status">
          <div className="team-member-list-item__email">
            {teamMember.user?.email}
          </div>

          {!isReadonly && !teamMember.isCurrentUser ? (
            <div className="team-member-list-item__status">
              <TeamMemberInvitationStatus
                teamMember={teamMember}
                onResendInvitation={onResendInvitation}
              />
            </div>
          ) : null}
        </div>

        {isReadonly ? (
          <div className="team-member-list-item__role">
            {t(`Team.Role.${teamMember.role as TeamRole}`)}
          </div>
        ) : null}

        {isReadonly ? null : (
          <div className="team-member-list-item__controls">
            <div className="team-member-list-item__role-select">
              <Select.Root
                label={t("Team.RoleOf", {
                  user: teamMember.user?.email || ""
                })}
                disabled={!!teamMember.isCurrentUser}
              >
                <Select.Input
                  data-testid={`change-team-role__${teamMember.user?.email}`}
                  value={teamMember.role as TeamRole}
                  options={options}
                  onChange={handleRoleChange}
                  className="select--minimal"
                />
                <Select.ErrorMessage />
              </Select.Root>
            </div>
            <div className="team-member-list-item__delete">
              <Button
                data-testid={`delete-team-member__${teamMember.user?.email}`}
                variant={ButtonVariant.TERTIARY}
                icon={IconName.BIN}
                iconSize={IconSize.TEXT}
                label={t("Team.Member.Actions.Delete", {
                  member: teamMember.user?.email
                })}
                disabled={!!teamMember.isCurrentUser}
                isIconOnly
                isDanger
                onClick={handleDelete}
              />
            </div>
          </div>
        )}
      </li>
    )
  }
)
